import { StandardMainArea } from '@salecycle/experimental/StandardMainArea';
import { EmptyListView } from './components/EmptyListView';
import { PopulatedListView } from './components/PopulatedListView';
import { useTranslation } from 'react-i18next';
import { useGetBulkRetargetingCampaignsQuery } from 'server/campaigns/queries/getBulkRetargetingCampaigns/useGetBulkRetargetingCampaignsQuery';
import { useParams } from 'react-router';
import { CreateCampaignModal } from './components/CreateCampaignModal/CreateCampaignModal';
import { useCallback, useState } from 'react';
import { AgentChat } from '@salecycle/experimental/Agent/AgentChat';
import { useFeatureFlags } from 'core/app/AppContext';
import { FloatingButton } from '@salecycle/experimental/Agent/FloatingButton';
import { useBulkRetargetingCampaignsCache } from '../CampaignEditor/hooks/useBulkRetargetingCampaignsCache';
import { AgentFeature, AgentQueryResponse } from '@salecycle/experimental/Agent/types';

export const CampaignList = () => {
  const { t } = useTranslation('campaigns');
  const { flagEnabled } = useFeatureFlags();
  const params = useParams<{ siteId: string }>();
  const [isAgentOpen, setIsAgentOpen] = useState(false);

  const campaignQuery = useGetBulkRetargetingCampaignsQuery(params);
  const { addCampaignToCache } = useBulkRetargetingCampaignsCache(params.siteId);

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const toggleModal = useCallback(() => setOpenCreateModal(!openCreateModal), [openCreateModal]);
  const toggleAgent = useCallback(() => setIsAgentOpen(prev => !prev), []);
  const showPopulatedView = !campaignQuery.isLoading && !!campaignQuery.data?.length;

  const updateCampaignList = useCallback(
    (data: AgentQueryResponse['data']) => data?.campaign?.id && addCampaignToCache(data.campaign),
    [addCampaignToCache],
  );

  return (
    <>
      <StandardMainArea
        isLoading={campaignQuery.isLoading}
        isError={campaignQuery.isError}
        heading={!campaignQuery.isLoading && campaignQuery.data?.length ? t('bulkRetargetingCampaigns.heading') : null}
        errorHeading={t('bulkRetargetingCampaigns.errorLoadingCampaigns')}
        headingButtonText={showPopulatedView && t('bulkRetargetingCampaigns.createCampaignButtonLabel')}
        headingButtonAria={showPopulatedView && t('bulkRetargetingCampaigns.createCampaignButtonLabel')}
        onHeadingButtonClicked={showPopulatedView && toggleModal}
        isAgentOpen={isAgentOpen}
        agentComponent={
          flagEnabled('ld-campy-assistant') && (
            <AgentChat
              siteId={params.siteId}
              feature={AgentFeature.BulkRetargeting}
              onClose={toggleAgent}
              onResponse={updateCampaignList}
            />
          )
        }
      >
        {showPopulatedView ? <PopulatedListView campaigns={campaignQuery.data} /> : <EmptyListView />}
      </StandardMainArea>
      <CreateCampaignModal isOpen={openCreateModal} onCloseButtonClicked={toggleModal} />
      {flagEnabled('ld-campy-assistant') && !isAgentOpen && (
        <FloatingButton onClick={toggleAgent} data-testid="floating-button">
          <img src="/images/campy_small.png" alt={t('campaignAssistant.agentName')} width={60} height={60} />
        </FloatingButton>
      )}
    </>
  );
};
